
import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, MessageSquare, StickyNote, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import DailyTile, { MealInfo } from '@/components/ui/daily-tile';
import { Recipe } from '@/types/recipe';
import { MealCompositionWithRecipes } from '@/types/mealComposition';
import { MealPlanWithDetails } from '@/types/mealPlan';
import { MealNote } from '@/types/mealNote';
import AddMealNoteDialog from './AddMealNoteDialog';
import { useMealSuggestions } from '@/hooks/useMealSuggestions';
import { useShare } from '@/hooks/useShare';
import CompactMealSuggestion from './CompactMealSuggestion';

interface MealPlannerTileProps {
  date: Date;
  mealPlans: MealPlanWithDetails[];
  mealNotes: MealNote[];
  recipes: Recipe[];
  compositions: MealCompositionWithRecipes[];
  isLoading: boolean;
  isSubmitting: boolean;
  hasError: boolean;
  today: Date;
  onRemoveMealPlan: (mealPlanId: string) => void;
  onRemoveMealNote: (mealNoteId: string) => void;
  onSaveMealNote: (noteText: string, mealType: 'breakfast' | 'lunch' | 'dinner') => void;
  onSaveMealPlan: (recipeId: string | null, compositionId?: string | null, mealType?: 'breakfast' | 'lunch' | 'dinner') => void;
}

const MealPlannerTile: React.FC<MealPlannerTileProps> = ({
  date,
  mealPlans,
  mealNotes,
  recipes,
  compositions,
  isLoading,
  isSubmitting,
  hasError,
  today,
  onRemoveMealPlan,
  onRemoveMealNote,
  onSaveMealNote,
  onSaveMealPlan
}) => {
  // Convert meal plans to MealInfo format with remove functionality and meal type
  const mealPlanMeals: MealInfo[] = mealPlans.map(mealPlan => {
    if (mealPlan.recipeId) {
      const recipe = recipes.find(r => r.id === mealPlan.recipeId);
      if (recipe) {
        return {
          type: 'recipe' as const,
          id: recipe.id,
          name: recipe.title,
          isQuickRecipe: recipe.isQuickRecipe,
          prepTime: recipe.prepTime,
          cookTime: recipe.cookTime,
          mealType: mealPlan.mealType,
          mealPlanId: mealPlan.id,
          onRemove: onRemoveMealPlan,
          authorFirstName: mealPlan.authorFirstName,
          imageUrl: recipe.imageUrl,
          storageImageUrl: recipe.storage_image_url,
          userImageUrl: recipe.user_image_url,
          storageImagePath: recipe.storage_image_path,
          userImagePath: recipe.user_image_path
        };
      }
    }
    
    if (mealPlan.mealCompositionId) {
      const composition = compositions.find(c => c.id === mealPlan.mealCompositionId);
      if (composition) {
        return {
          type: 'composition' as const,
          id: composition.id,
          name: composition.name,
          recipeCount: composition.recipes.length,
          mealType: mealPlan.mealType,
          mealPlanId: mealPlan.id,
          onRemove: onRemoveMealPlan,
          authorFirstName: mealPlan.authorFirstName
        };
      }
    }
    
    return null;
  }).filter(Boolean) as MealInfo[];

  // Convert meal notes to MealInfo format
  const mealNoteMeals: MealInfo[] = mealNotes.map(mealNote => ({
    type: 'note' as const,
    id: mealNote.id,
    name: mealNote.noteText,
    mealType: mealNote.mealType as 'breakfast' | 'lunch' | 'dinner',
    mealNoteId: mealNote.id,
    onRemoveNote: onRemoveMealNote,
    authorFirstName: mealNote.authorFirstName
  }));

  // Combine both meal plans and meal notes
  const allMeals: MealInfo[] = [...mealPlanMeals, ...mealNoteMeals];
  
  // Helper function to determine the best default meal mode
  const getDefaultMealMode = (): 'breakfast' | 'lunch' | 'dinner' => {
    // Priority order: dinner → breakfast → lunch (dinner is the heart of Forkast)
    const mealTypes: ('breakfast' | 'lunch' | 'dinner')[] = ['dinner', 'breakfast', 'lunch'];
    
    for (const mealType of mealTypes) {
      if (allMeals.some(meal => meal.mealType === mealType)) {
        return mealType;
      }
    }
    
    // Fallback to dinner if no meals exist
    return 'dinner';
  };
  
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [acceptingSuggestion, setAcceptingSuggestion] = useState(false);
  const [mealMode, setMealMode] = useState<'breakfast' | 'lunch' | 'dinner'>(getDefaultMealMode());
  const dateString = format(date, 'yyyy-MM-dd');
  const hasMeals = mealPlans.length > 0;
  
  // Load AI suggestions for this date and meal type (only for lunch and dinner)
  const { suggestion, isLoading: suggestionsLoading, dismissSuggestion } = useMealSuggestions(
    date, 
    mealMode === 'breakfast' ? 'dinner' : mealMode
  );
  
  // Check if there are meals for the current mode (for button logic)
  const currentModeMeals = allMeals.filter(meal => meal.mealType === mealMode).length > 0;
  
  // Check if there are any breakfast, lunch, or dinner items for badge display
  const hasBreakfast = allMeals.some(meal => meal.mealType === 'breakfast');
  const hasLunch = allMeals.some(meal => meal.mealType === 'lunch');
  const hasDinner = allMeals.some(meal => meal.mealType === 'dinner');

  const isPast = date < today && !format(date, 'yyyy-MM-dd').includes(format(today, 'yyyy-MM-dd'));
  const isEditable = !isPast;
  
  // Show AI suggestions for empty dates that are editable and for lunch or dinner modes
  const showAISuggestions = isEditable && !currentModeMeals && (mealMode === 'lunch' || mealMode === 'dinner') && suggestion && !suggestionsLoading;

  const handleAcceptSuggestion = async () => {
    if (!suggestion) return;
    
    setAcceptingSuggestion(true);
    try {
      if (suggestion.existingRecipe) {
        await onSaveMealPlan(suggestion.existingRecipe.id, null, mealMode);
      } else if (suggestion.isFromCollection && suggestion.recipeId) {
        await onSaveMealPlan(suggestion.recipeId, null, mealMode);
      }
    } finally {
      setAcceptingSuggestion(false);
    }
  };

  const handleAcceptSimilarSuggestion = async (recipeId: string) => {
    setAcceptingSuggestion(true);
    try {
      await onSaveMealPlan(recipeId, null, mealMode);
    } finally {
      setAcceptingSuggestion(false);
    }
  };

  // Only show remove functionality for editable dates
  const mealsWithRemove = isEditable ? allMeals : allMeals.map(meal => ({
    ...meal,
    mealPlanId: undefined,
    mealNoteId: undefined,
    onRemove: undefined,
    onRemoveNote: undefined
  }));


  const footer = isEditable ? (
    <div className="flex flex-col gap-2">
      {/* Show AI Suggestion first if available */}
      {showAISuggestions && (
        <CompactMealSuggestion
          suggestion={suggestion}
          onAccept={handleAcceptSuggestion}
          onDismiss={() => dismissSuggestion(suggestion.id)}
          isAccepting={acceptingSuggestion}
          onAcceptSimilar={handleAcceptSimilarSuggestion}
        />
      )}
      
      <>
        <Link to={`/meal-selection/${dateString}?mealType=${mealMode}`} className="flex-1 min-w-[140px]">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            disabled={isSubmitting || isLoading || hasError}
          >
            <Plus className="h-4 w-4 mr-2" />
            {currentModeMeals ? `Add to ${mealMode.charAt(0).toUpperCase() + mealMode.slice(1)} Plan` : `Select ${mealMode.charAt(0).toUpperCase() + mealMode.slice(1)}`}
          </Button>
        </Link>
        <Button
          variant="outline"
          size="sm"
          className="flex-1 min-w-[140px]"
          onClick={() => setShowNoteDialog(true)}
          disabled={isSubmitting || isLoading || hasError}
        >
          <StickyNote className="h-4 w-4 mr-2" />
          Add {mealMode.charAt(0).toUpperCase() + mealMode.slice(1)} Note
        </Button>
      </>
      {/* Only show AI Suggestions button if no meals in current mode and suggestions exist */}
      {!currentModeMeals && !showAISuggestions && (mealMode === 'lunch' || mealMode === 'dinner') && suggestion && (
        <Link to="/meal-suggestions" className="w-full">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            disabled={isSubmitting || isLoading || hasError}
          >
            <Sparkles className="h-4 w-4 mr-2" />
            {mealMode.charAt(0).toUpperCase() + mealMode.slice(1)} Suggestions
          </Button>
        </Link>
      )}
    </div>
  ) : (() => {
    // Find a recipe meal plan for the current mode to enable review button
    const currentModeRecipeMealPlan = mealPlans.find(mp => mp.mealType === mealMode && mp.recipeId);
    
    return currentModeRecipeMealPlan ? (
      <div className="flex gap-2 flex-wrap">
        <Link to={`/recipe/${currentModeRecipeMealPlan.recipeId}#recipe-feedback`} className="flex-1">
          <Button variant="outline" size="sm" className="w-full flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Leave a review
          </Button>
        </Link>
      </div>
    ) : (
      <Button variant="outline" size="sm" disabled className="opacity-50">
        No {mealMode.charAt(0).toUpperCase() + mealMode.slice(1)} Selected
      </Button>
    );
  })();

  return (
    <>
      <DailyTile
        date={date}
        meals={mealsWithRemove}
        isLoading={isLoading}
        footer={footer}
        mealMode={mealMode}
        onMealTypeChange={(newMealType) => setMealMode(newMealType)}
        hasBreakfast={hasBreakfast}
        hasLunch={hasLunch}
        hasDinner={hasDinner}
        isEditable={isEditable}
        hideBadge={true}
      />
      <AddMealNoteDialog
        open={showNoteDialog}
        onOpenChange={setShowNoteDialog}
        onSave={(noteText) => {
          onSaveMealNote(noteText, mealMode);
        }}
        mealType={mealMode}
        isLoading={isSubmitting}
      />
    </>
  );
};

export default MealPlannerTile;
